import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import { selectRoles } from "../../../features/roleSlice";
import { IconTile, PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState } from "../../../components/UI/States";
import {
  NAV_SECTIONS,
  sectionGroups,
  checkRoles,
  matchesQuery,
} from "../../../components/Navigation/SideNav";
import { pageVisual } from "../../../components/Home/homeIcons";

// The Admin section of NAV_SECTIONS is a hub: the drawer shows it as one row
// that opens this page, and every page in it is a card here. Names, paths,
// roles and descriptions all come from that section, so a new admin page
// only needs adding there (see "Side nav" in CLAUDE.md).
const ADMIN_SECTION = NAV_SECTIONS.find((section) => section.id === "admin");

// One line under each group heading, keyed by the nav group title.
const GROUP_BLURBS = {
  "People & Access": "Accounts, roles, and who can open which pages.",
  "Node Apps": "Feedback, mobile installs, and the assistant for the Node apps.",
  Communications: "Email signatures, the TVs at each branch, and the public website.",
  "Shop Floor": "Punch clocks and job schedules for the PCW and Mill shops.",
};

// Pages in a team's section that only these roles can open are admin pages
// too. They stay in their team's section of the menu, and are also listed
// here under "Team pages" so this page links to every admin tool.
const LEADERSHIP_ROLES = ["Admin", "Executive Team", "IT"];

const isListed = (item) => !item.disabled && !item.navHidden;

const ADMIN_GROUPS = sectionGroups(ADMIN_SECTION)
  .map((group) => ({
    title: group.title,
    blurb: GROUP_BLURBS[group.title],
    visual: pageVisual(null, group.title),
    items: group.items.filter(
      (item) => isListed(item) && item.path !== ADMIN_SECTION.hubPath
    ),
  }))
  .filter((group) => group.items.length > 0);

const TEAM_GROUP = {
  title: "Team pages",
  blurb: "Admin and leadership pages that live in their team's section of the menu.",
  visual: { icon: GroupsRoundedIcon, tone: "sky" },
  items: NAV_SECTIONS.filter((section) => !section.hubPath).flatMap((section) =>
    sectionGroups(section).flatMap((group) =>
      group.items
        .filter(
          (item) =>
            isListed(item) &&
            item.roles?.length > 0 &&
            item.roles.every((role) => LEADERSHIP_ROLES.includes(role))
        )
        .map((item) => ({ ...item, sectionTitle: section.title }))
    )
  ),
};

const GROUPS = [...ADMIN_GROUPS, TEAM_GROUP];

const AdminCard = ({ item }) => {
  const { icon, tone } = pageVisual(item.path);
  return (
    <Link
      to={item.path}
      className="app-card group flex h-full items-start gap-4 rounded-2xl border p-4 transition duration-200 hover:-translate-y-0.5 hover:border-sky-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
    >
      <IconTile icon={icon} tone={tone} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="font-semibold leading-snug tracking-tight">{item.name}</p>
          {item.sectionTitle && (
            <span className="app-chip text-[11px]">{item.sectionTitle}</span>
          )}
        </div>
        {item.description && (
          <p className="app-muted mt-1 text-sm leading-relaxed">
            {item.description}
          </p>
        )}
      </div>
      <ChevronRightRoundedIcon
        className="app-muted mt-0.5 shrink-0 opacity-40 transition group-hover:translate-x-0.5 group-hover:text-[color:var(--app-accent)] group-hover:opacity-100"
        sx={{ fontSize: 20 }}
      />
    </Link>
  );
};

const GroupSection = ({ group }) => {
  const headingId = `admin-group-${group.title.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-3 flex items-center gap-3">
        <IconTile icon={group.visual.icon} tone={group.visual.tone} size="sm" />
        <div className="min-w-0">
          <h2
            id={headingId}
            className="text-lg font-semibold tracking-tight text-[color:var(--app-text)]"
          >
            {group.title}
          </h2>
          {group.blurb && <p className="app-muted text-sm">{group.blurb}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {group.items.map((item) => (
          <AdminCard key={item.path} item={item} />
        ))}
      </div>
    </section>
  );
};

/**
 * Admin Center (/admin): every admin tool on the site as cards, grouped like
 * the old Admin menu section, showing only the pages the viewer can open.
 * The route is open to everyone on purpose — Page Access grants on a single
 * admin page should get you here too — so someone with nothing to manage
 * just sees an empty state.
 */
const AdminCenter = () => {
  const role = useSelector(selectRoles);
  const userRoles = useMemo(() => role?.roles || [], [role]);
  const grantedPaths = role?.grantedPaths;
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();

  const visibleGroups = useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          checkRoles(item.roles, userRoles, item.path, grantedPaths)
        ),
      })).filter((group) => group.items.length > 0),
    [userRoles, grantedPaths]
  );

  const shownGroups = useMemo(() => {
    if (!trimmedQuery) return visibleGroups;
    return visibleGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          matchesQuery(
            [
              item.name,
              item.description || "",
              ...(item.keywords || []),
              group.title,
              item.sectionTitle || "",
            ]
              .join(" ")
              .toLowerCase(),
            trimmedQuery
          )
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [visibleGroups, trimmedQuery]);

  return (
    <PageShell size="xl">
      <PageHeader
        icon={AdminPanelSettingsIcon}
        tone="slate"
        eyebrow="Admin"
        title="Admin Center"
        description="Every admin tool on Node in one place. You only see the pages your roles can open."
      />

      {visibleGroups.length === 0 ? (
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={AdminPanelSettingsIcon}
            title="Nothing to manage here yet"
            description="Admin pages show up here once you have a role that can open them. If you think you should, contact HelpDesk."
            action={
              <Button component={Link} to="/" variant="outlined">
                Back to Home
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <TextField
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find an admin tool… (e.g. users, signage, codes, hours)"
            inputProps={{ "aria-label": "Find an admin tool" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {shownGroups.length ? (
            <div className="mt-8 space-y-9">
              {shownGroups.map((group) => (
                <GroupSection key={group.title} group={group} />
              ))}
            </div>
          ) : (
            <div className="app-card mt-6 rounded-2xl border">
              <EmptyState
                icon={SearchOffIcon}
                title={`No admin tools match “${trimmedQuery}”`}
                description="Try a different word — users, roles, signage, codes, or hours."
                action={
                  <Button variant="outlined" onClick={() => setQuery("")}>
                    Clear search
                  </Button>
                }
              />
            </div>
          )}
        </>
      )}
    </PageShell>
  );
};

export default AdminCenter;
